import { createFileRoute } from "@tanstack/react-router";
import { PEOPLE_ORIGIN, marketingHead } from "@/lib/marketing/seo";
import { PUBLISHER, WEBSITE_ID, breadcrumbs } from "@/lib/marketing/ppl-schema";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { SectionHeading } from "@/components/marketing/site/section-heading";
import { MediaFrame } from "@/components/marketing/site/media-frame";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { AboutThesis } from "@/components/marketing/site/about-thesis";
import { ProofRequest } from "@/components/marketing/proof/proof-request";
import type { ProofRequestReceipt } from "@/components/marketing/proof/proof-request-types";
import { Heart, Compass, ShieldCheck, Layers } from "@/lib/public-icons";
const activityImg = "/marketing/activity_senior.jpg";

// The organization claims the company page in JSON-LD, not a person. The schema carries the founder
// and customer answers into extracted copy for readers who never scroll.
const ABOUT_SCHEMA = {
  "@type": "AboutPage",
  name: "About PEOPLE AI Workforce (PPL AI)",
  url: `${PEOPLE_ORIGIN}/about`,
  description:
    "PEOPLE AI Workforce was built by owner-operator Ashok Gupta and runs in production today at Reunion Inn Assisted Living and Pine Lodge Assisted Living, operated with US Senior Care.",
  publisher: PUBLISHER,
  isPartOf: { "@id": WEBSITE_ID },
  /* The company itself, which every page of the arm describes the same way
     (lib/marketing/ppl-schema.ts): the founder, the address and the LinkedIn
     page are on that node. */
  mainEntity: PUBLISHER,
};

const values = [
  {
    icon: Heart,
    title: "Residents first",
    description:
      "Every feature is judged by one question: does it give caregivers more time and attention for the people in their care?",
  },
  {
    icon: Compass,
    title: "Built with operators",
    description:
      "Daily roll, checklists and auth tracking exist because real communities told us how they actually work.",
  },
  {
    icon: ShieldCheck,
    title: "Trust by default",
    description:
      "We treat PHI as a responsibility, not a checkbox, security and privacy are designed in from the start.",
  },
  {
    icon: Layers,
    title: "Honest and modular",
    description:
      "Add what you need, and never pay for modules you don't use. No lock-in, no surprises.",
  },
];

/* A reference customer a buyer can call, named plainly, since it is the highest-intent ask on this
 * page. Both lines are true of the two communities they name. */
const referenceReceipts: ProofRequestReceipt[] = [
  {
    title: "Who you would be calling",
    description:
      "The people who run Reunion Inn Assisted Living and Pine Lodge Assisted Living, both operated with US Senior Care, named here so you can look them up before you dial.",
  },
  {
    title: "What it is not",
    description:
      "Not a recorded case study, not a logo on a wall, and not a demo. Another owner, on the phone, answering whatever you ask.",
  },
];

const stats = [
  { value: "8+", label: "integrated modules" },
  { value: "1 → 100", label: "communities, one platform" },
  { value: "14 days", label: "to go live, not months" },
  { value: "30 min", label: "to see it in a demo" },
];

function MarketingAbout() {
  return (
    <MarketingShell>
      <AboutThesis />

      {/* The reference call, as a request rather than a sentence. */}
      <ProofRequest
        formTag="reference-call"
        testId="reference-call"
        id="reference-call"
        eyebrow="Talk to an operator first"
        title="Ask another owner before you ask us"
        subtitle="You should be able to ask someone who runs a community on this system what a med pass actually looks like on it, what the first month was, and what they would do differently. Tell us who you are and we'll arrange the call."
        receipts={referenceReceipts}
        noteLabel="Anything you want to ask them? (optional)"
        notePlaceholder="e.g. how the move off paper went, or what a state survey was like on it…"
        submitLabel="Arrange the call"
        successTitle="We'll set the call up"
        successBody="Your request is with the team, carrying the community you typed and anything you asked to cover. Nothing is signed to have the conversation."
        footnote="A call with another operator. Not a sales call."
      />

      {/* Values */}
      <section className="border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <Reveal>
            <SectionHeading eyebrow="What we believe" title="The principles behind the product" />
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={(i % 2) * 80}>
                <div className="flex h-full gap-4 rounded-xl border border-border/60 bg-background p-6 hover-elevate">
                  <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <v.icon className="size-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold tracking-tight">{v.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">{v.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats + image */}
      <section className="border-t border-border/60">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-20 md:grid-cols-2 md:py-28">
          <Reveal>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className="bg-background px-5 py-8 text-center"
                  data-testid={`about-stat-${i}`}
                >
                  <div className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
                    {s.value}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={80}>
            <MediaFrame
              src={activityImg}
              alt="Residents together at a community activity"
              testId="img-about-activity"
            />
          </Reveal>
        </div>
      </section>

      {/* The generic closing ask stays. It is no longer the only one on the
          page, and it is no longer where a reader who wants an operator's phone
          number has to end up. */}
      <MarketingCta
        title="Join the communities running on PEOPLE AI Workforce"
        subtitle="Book a demo and see what your team can do with their time back, or talk to an operator running it today, above."
        secondaryLabel="Talk to us"
        secondaryHref="/contact"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/about")({
  component: MarketingAbout,
  head: () => ({
    ...marketingHead({
      title: "About PEOPLE AI Workforce (PPL AI): why it exists",
      description:
        "Software stored the work. PEOPLE AI Workforce does it: an operating system built for AI to operate, with humans assisting, and an AI workforce that runs the administrative company so your people can be people.",
      path: "/v2/about",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
      schema: [ABOUT_SCHEMA, breadcrumbs({ name: "About", path: "/about" })],
    }),
  }),
});
